import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import {
  DollarSign,
  Plus,
  CreditCard,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  Calendar,
  Zap,
  Building2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Payments = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [recurringPayments, setRecurringPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRecurringDialogOpen, setIsRecurringDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    property_id: "",
    amount: "",
    payment_type: "invoice",
    payment_rail: "ach",
    description: "",
  });
  const [recurringFormData, setRecurringFormData] = useState({
    property_id: "",
    amount: "",
    payment_type: "invoice",
    payment_rail: "ach",
    day_of_month: "1",
    description: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, propertiesRes, bankRes, recurringRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments`),
        axios.get(`${API_URL}/api/projects`),
        axios.get(`${API_URL}/api/plaid/bank-account`),
        axios.get(`${API_URL}/api/recurring-payments`),
      ]);
      setPayments(paymentsRes.data);
      setProjects(propertiesRes.data);
      setBankAccount(bankRes.data);
      setRecurringPayments(recurringRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    
    if (!bankAccount) {
      toast.error("Pengagement link a bank account first");
      return;
    }

    setIsProcessing(true);
    
    try {
      // Create payment
      const paymentRes = await axios.post(`${API_URL}/api/payments`, {
        property_id: formData.property_id,
        amount: parseFloat(formData.amount),
        payment_type: formData.payment_type,
        payment_rail: formData.payment_rail,
        description: formData.description || `${formData.payment_type} payment`,
      });
      
      // Authorize transfer
      const authRes = await axios.post(`${API_URL}/api/transfers/authorize`, {
        payment_id: paymentRes.data.id,
        bank_account_id: bankAccount.id,
      });
      
      if (authRes.data.decision !== "approved") {
        toast.error(`Payment not approved: ${authRes.data.rationale_code || "Unknown reason"}`);
        return;
      }
      
      // Create transfer
      await axios.post(`${API_URL}/api/transfers/create`, {
        authorization_id: authRes.data.authorization_id,
      });
      
      toast.success("Payment initiated successfully!");
      setIsDialogOpen(false);
      setFormData({
        property_id: "",
        amount: "",
        payment_type: "invoice",
        payment_rail: "ach",
        description: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process payment");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    
    if (!bankAccount) {
      toast.error("Pengagement link a bank account first");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/recurring-payments`, {
        property_id: recurringFormData.property_id,
        amount: parseFloat(recurringFormData.amount),
        payment_type: recurringFormData.payment_type,
        payment_rail: recurringFormData.payment_rail,
        day_of_month: parseInt(recurringFormData.day_of_month),
        description: recurringFormData.description,
      });
      
      toast.success("Recurring payment set up successfully!");
      setIsRecurringDialogOpen(false);
      setRecurringFormData({
        property_id: "",
        amount: "",
        payment_type: "invoice",
        payment_rail: "ach",
        day_of_month: "1",
        description: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set up recurring payment");
    }
  };

  const handleCancelRecurring = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/recurring-payments/${id}`);
      toast.success("Recurring payment cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel recurring payment");
    }
  };

  const handleCompleteTransfer = async (transferId) => {
    try {
      await axios.post(`${API_URL}/api/transfers/${transferId}/complete`);
      toast.success("Transfer marked as complete");
      fetchData();
    } catch (error) {
      toast.error("Failed to complete transfer");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
      case "pending":
        return <Clock className="w-4 h-4 text-amber-600" />;
      case "processing":
        return <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />;
      case "failed":
      case "returned":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-stone-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "badge-pending",
      processing: "bg-blue-100 text-blue-800 border border-blue-200",
      completed: "badge-completed",
      failed: "badge-failed",
      returned: "bg-orange-100 text-orange-800 border border-orange-200",
    };
    return variants[status] || "bg-stone-100 text-stone-800";
  };

  const getRailIcon = (rail) => {
    switch (rail) {
      case "rtp":
      case "fednow":
        return <Zap className="w-3 h-3" />;
      default:
        return <CreditCard className="w-3 h-3" />;
    }
  };

  const getRailLabel = (rail) => {
    const labels = {
      ach: "ACH (1-3 days)",
      "same-day-ach": "Same-Day ACH",
      rtp: "RTP (Instant)",
      fednow: "FedNow (Instant)",
    };
    return labels[rail] || rail;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="payments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Payments
            </h1>
            <p className="text-stone-600 mt-1">
              {user?.role === "vendor" ? "Track incoming payments" : "Make and manage your payments"}
            </p>
          </div>
          {user?.role === "employee" && (
            <div className="flex gap-2">
              <Dialog open={isRecurringDialogOpen} onOpenChange={setIsRecurringDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="setup-recurring-btn">
                    <Calendar className="w-4 h-4 mr-2" />
                    Auto-Pay
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Set Up Auto-Pay</DialogTitle>
                    <DialogDescription>
                      Automatically pay your invoice on a specific day each month.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateRecurring} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Project</Label>
                      <Select
                        value={recurringFormData.property_id}
                        onValueChange={(value) => {
                          const prop = projects.find(p => p.id === value);
                          setRecurringFormData(prev => ({
                            ...prev,
                            property_id: value,
                            amount: prop?.rent_amount?.toString() || "",
                          }));
                        }}
                      >
                        <SelectTrigger data-testid="recurring-project-select">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name} - {formatCurrency(project.rent_amount)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={recurringFormData.amount}
                          onChange={(e) => setRecurringFormData(prev => ({ ...prev, amount: e.target.value }))}
                          required
                          data-testid="recurring-amount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Day of Month</Label>
                        <Select
                          value={recurringFormData.day_of_month}
                          onValueChange={(value) => setRecurringFormData(prev => ({ ...prev, day_of_month: value }))}
                        >
                          <SelectTrigger data-testid="recurring-day-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 5, 10, 15, 20, 25].map((day) => (
                              <SelectItem key={day} value={day.toString()}>
                                {day}
                                {day === 1 ? "st" : day === 5 ? "th" : day === 10 ? "th" : day === 15 ? "th" : day === 20 ? "th" : "th"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <Select
                        value={recurringFormData.payment_rail}
                        onValueChange={(value) => setRecurringFormData(prev => ({ ...prev, payment_rail: value }))}
                      >
                        <SelectTrigger data-testid="recurring-rail-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ach">Standard ACH (1-3 days)</SelectItem>
                          <SelectItem value="same-day-ach">Same-Day ACH</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsRecurringDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="recurring-submit-btn">
                        Set Up Auto-Pay
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="make-payment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Make Payment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Make a Payment</DialogTitle>
                    <DialogDescription>
                      Send a payment to your vendor.
                    </DialogDescription>
                  </DialogHeader>
                  {!bankAccount ? (
                    <div className="py-6 text-center">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                      <p className="text-stone-900 font-medium mb-2">No Bank Account Linked</p>
                      <p className="text-sm text-stone-600 mb-4">
                        Pengagement link a bank account before making payments.
                      </p>
                      <Button onClick={() => window.location.href = "/bank-account"}>
                        Link Bank Account
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreatePayment} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Project</Label>
                        <Select
                          value={formData.property_id}
                          onValueChange={(value) => {
                            const prop = projects.find(p => p.id === value);
                            setFormData(prev => ({
                              ...prev,
                              property_id: value,
                              amount: prop?.rent_amount?.toString() || "",
                            }));
                          }}
                        >
                          <SelectTrigger data-testid="payment-project-select">
                            <SelectValue placeholder="Select project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={formData.amount}
                            onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                            required
                            data-testid="payment-amount-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select
                            value={formData.payment_type}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, payment_type: value }))}
                          >
                            <SelectTrigger data-testid="payment-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="invoice">Invoice</SelectItem>
                              <SelectItem value="security_deposit">Retainer</SelectItem>
                              <SelectItem value="late_fee">Late Fee</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Payment Speed</Label>
                        <Select
                          value={formData.payment_rail}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, payment_rail: value }))}
                        >
                          <SelectTrigger data-testid="payment-rail-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ach">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4" />
                                Standard ACH (1-3 days)
                              </div>
                            </SelectItem>
                            <SelectItem value="same-day-ach">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4" />
                                Same-Day ACH
                              </div>
                            </SelectItem>
                            <SelectItem value="rtp">
                              <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4" />
                                RTP - Instant
                              </div>
                            </SelectItem>
                            <SelectItem value="fednow">
                              <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4" />
                                FedNow - Instant
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Description (optional)</Label>
                        <Input
                          placeholder="e.g., January invoice"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          data-testid="payment-description-input"
                        />
                      </div>
                      <div className="p-3 bg-stone-50 rounded-lg">
                        <p className="text-sm text-stone-600">Paying from:</p>
                        <p className="font-medium text-stone-900">
                          {bankAccount.institution_name} •••• {bankAccount.mask}
                        </p>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="bg-emerald-900 hover:bg-emerald-800"
                          disabled={isProcessing}
                          data-testid="payment-submit-btn"
                        >
                          {isProcessing ? "Processing..." : "Pay Now"}
                        </Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <Card data-testid="all-payments-card">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Payment History</CardTitle>
                <CardDescription>All your payment transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-stone-900 mb-2">No payments yet</h3>
                    <p className="text-stone-600">
                      {user?.role === "employee"
                        ? "Make your first payment to get started."
                        : "Payments from employees will appear here."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Project</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                          {user?.role === "employee" && <TableHead></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell className="font-medium">{payment.property_name}</TableCell>
                            <TableCell className="capitalize">{payment.payment_type.replace("_", " ")}</TableCell>
                            <TableCell className="font-semibold text-emerald-700">
                              {formatCurrency(payment.amount)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-xs text-stone-600">
                                {getRailIcon(payment.payment_rail)}
                                {getRailLabel(payment.payment_rail)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${getStatusBadge(payment.status)} flex items-center gap-1 w-fit`}>
                                {getStatusIcon(payment.status)}
                                {payment.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-stone-500 text-sm">
                              {formatDate(payment.created_at)}
                            </TableCell>
                            {user?.role === "employee" && payment.status === "processing" && payment.transfer_id && (
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCompleteTransfer(payment.transfer_id)}
                                  data-testid={`complete-transfer-${payment.id}`}
                                >
                                  Simulate Complete
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending" className="mt-6">
            <Card data-testid="pending-payments-card">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Pending Payments</CardTitle>
                <CardDescription>Payments awaiting completion</CardDescription>
              </CardHeader>
              <CardContent>
                {payments.filter(p => p.status === "pending" || p.status === "processing").length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-stone-900 mb-2">All caught up!</h3>
                    <p className="text-stone-600">No pending payments.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payments
                      .filter(p => p.status === "pending" || p.status === "processing")
                      .map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                              <Clock className="w-6 h-6 text-amber-700" />
                            </div>
                            <div>
                              <p className="font-medium text-stone-900">{payment.property_name}</p>
                              <p className="text-sm text-stone-500 capitalize">
                                {payment.payment_type.replace("_", " ")} • {getRailLabel(payment.payment_rail)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-stone-900">{formatCurrency(payment.amount)}</p>
                            <Badge className={getStatusBadge(payment.status)}>{payment.status}</Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="recurring" className="mt-6">
            <Card data-testid="recurring-payments-card">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Recurring Payments</CardTitle>
                <CardDescription>Your auto-pay schedules</CardDescription>
              </CardHeader>
              <CardContent>
                {recurringPayments.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-stone-900 mb-2">No auto-pay set up</h3>
                    <p className="text-stone-600 mb-4">
                      Set up recurring payments to never miss invoice.
                    </p>
                    {user?.role === "employee" && (
                      <Button
                        onClick={() => setIsRecurringDialogOpen(true)}
                        className="bg-emerald-900 hover:bg-emerald-800 rounded-full"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Set Up Auto-Pay
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recurringPayments.map((recurring) => (
                      <div key={recurring.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <RefreshCw className="w-6 h-6 text-emerald-700" />
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{recurring.property_name}</p>
                            <p className="text-sm text-stone-500">
                              Every month on the {recurring.day_of_month}
                              {recurring.day_of_month === 1 ? "st" : recurring.day_of_month === 2 ? "nd" : recurring.day_of_month === 3 ? "rd" : "th"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(recurring.amount)}</p>
                            <Badge className={recurring.is_active ? "badge-completed" : "bg-stone-100 text-stone-600"}>
                              {recurring.is_active ? "Active" : "Cancelled"}
                            </Badge>
                          </div>
                          {recurring.is_active && user?.role === "employee" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleCancelRecurring(recurring.id)}
                              data-testid={`cancel-recurring-${recurring.id}`}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Payments;
